// Lọc danh sách đơn nợ trong đơn Thu nợ (OrderDetailThuNo). File thuần, không import global/Global để test được.
export type TPaymentStatus = 'both-done' | 'bank-pending' | 'cash-pending' | 'unknown';

// Server có lúc trả tiền âm (vd dòng "Tồn ban đầu" có VAT -1,000,000) -> coi như 0, cả khi hiển thị lẫn tính toán/gửi đi.
// Gọi ngay khi nhận dữ liệu đơn nợ (processOrderData / serverItems) để mọi chỗ phía sau dùng chung số 0 đó.
// tienNo giữ nguyên số server trả (đã là nợ cuối cùng), chỉ chặn âm.
// Vd: tienSanPham 10,000,000, thuVat -1,000,000, tienNo 10,000,000 -> thuVat 0, tienNo vẫn 10,000,000.
const PART_FIELDS = ['thuVat', 'thuChenhLech', 'goiCuoc', 'tienSanPham', 'tienPhi', 'tienCong'];
export const clampDebtAmounts = (order: any): any => {
  if (!order) return order;
  const next = { ...order };
  PART_FIELDS.forEach(f => {
    if (Number(next[f] || 0) < 0) next[f] = 0;
  });
  if (Array.isArray(next.childItems)) next.childItems = next.childItems.map(clampDebtAmounts);
  if (Number(next.tienNo || 0) < 0) next.tienNo = 0;
  return next;
};

// Trạng thái thu tiền của 1 đơn nợ
export function getPaymentStatus(order: any): TPaymentStatus {
  const cash = order?.isCashCollected;
  const bank = order?.isBankCollected;
  if (cash && bank) return 'both-done';
  if (cash && !bank) return 'bank-pending';
  if (!cash && bank) return 'cash-pending';
  return 'unknown';
}

// Trùng chữ với badge hiển thị trong getRowStyle
const PAYMENT_STATUS_LABEL: Partial<Record<TPaymentStatus, string>> = {
  'both-done': 'Đã thu đủ',
  'bank-pending': 'Chờ xác nhận CK',
  'cash-pending': 'Chưa thu TM'
};

const EMPTY_LABEL = '—';

export type TDebtSelectKey = 'loai' | 'httt' | 'nvkt' | 'nguoiTao';

export interface IDebtFilter {
  maDon: string;
  ngay: string;
  loai: string[];
  httt: string[];
  nvkt: string[];
  nguoiTao: string[];
}

export const EMPTY_DEBT_FILTER: IDebtFilter = { maDon: '', ngay: '', loai: [], httt: [], nvkt: [], nguoiTao: [] };

const SELECT_KEYS: TDebtSelectKey[] = ['loai', 'httt', 'nvkt', 'nguoiTao'];

// Giá trị của 1 đơn theo từng cột chọn. Trả mảng vì 1 đơn có thể nhiều KT, hoặc vừa HTTT vừa trạng thái thu
const SELECT_VALUES: Record<TDebtSelectKey, (o: any) => string[]> = {
  loai: o => [o?.orderType || EMPTY_LABEL],
  httt: o => {
    const status = PAYMENT_STATUS_LABEL[getPaymentStatus(o)];
    return status ? [o?.hinhThucThanhToan || EMPTY_LABEL, status] : [o?.hinhThucThanhToan || EMPTY_LABEL];
  },
  nvkt: o => (o?.staffExecutes?.length ? o.staffExecutes : [EMPTY_LABEL]),
  nguoiTao: o => [o?.nguoiTao || EMPTY_LABEL]
};

const includesText = (value: any, query: string) => {
  const q = query.trim().toLowerCase();
  return (
    !q ||
    String(value ?? '')
      .toLowerCase()
      .includes(q)
  );
};

export const matchDebtFilter = (order: any, filter: IDebtFilter): boolean =>
  includesText(order?.maDonHang, filter.maDon) &&
  includesText(order?.ngay, filter.ngay) &&
  SELECT_KEYS.every(key => !filter[key].length || SELECT_VALUES[key](order).some(v => filter[key].includes(v)));

export const isDebtFilterActive = (filter: IDebtFilter): boolean =>
  !!filter.maDon.trim() || !!filter.ngay.trim() || SELECT_KEYS.some(key => filter[key].length > 0);

// Lựa chọn cho các ô Select, lấy từ chính danh sách đơn đang có
export const buildDebtFilterOptions = (orders: any[]): Record<TDebtSelectKey, string[]> => {
  const result = {} as Record<TDebtSelectKey, string[]>;
  SELECT_KEYS.forEach(key => {
    const values = new Set<string>();
    (orders || []).forEach(o => SELECT_VALUES[key](o).forEach(v => values.add(v)));
    result[key] = Array.from(values).sort((a, b) => a.localeCompare(b, 'vi'));
  });
  return result;
};
